/* Read-only presentation subscriber. Never writes to a product form or commerce state. */
(() => {
  if (customElements.get('rp-v2-pdp')) return;
  class RopetPDP extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const section = this.closest('[id^="shopify-section-"]');
      const prefix = section?.id.replace('shopify-section-', '').split('__')[0];
      this.ownerId = `MainProduct-${prefix}__main`;
      this.bindOwner();
      window.addEventListener('blur', () => this.resetGaze(), {signal});
      document.addEventListener('visibilitychange', () => {if(document.hidden)this.resetGaze();}, {signal});
      this.onVariant = event => {
        if (!this.ownerSection || event.detail?.sectionId !== this.ownerSection) return;
        this.showVariant(String(event.detail?.variant?.id || ''));
      };
      document.addEventListener('afterVariantChanged', this.onVariant, {signal});
      if(this.hasAttribute('data-rp-v2-profiles')){
        window.addEventListener('popstate',()=>{cancelAnimationFrame(this.profileFrame);this.profileFrame=requestAnimationFrame(()=>this.bindOwner());},{signal});
        this.addEventListener('shopify:block:select',event=>{if(this.dataset.rpV2Editor!=='true')return;const key=event.target.closest('[data-rp-v2-profile-key]')?.dataset.rpV2ProfileKey;if(key){this.editorProfile=key;this.showVariant(this.owner?.querySelector('.product-variant-id')?.value||'');}},{signal});
        this.addEventListener('shopify:block:deselect',()=>{this.editorProfile=null;this.showVariant(this.owner?.querySelector('.product-variant-id')?.value||'');},{signal});
      }
      document.addEventListener('shopify:section:load', event => {
        if (event.target?.querySelector?.(`[id="${this.ownerId}"]`)) this.bindOwner();
      }, {signal});
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-rp-v2-wave') && event.detail === 0) this.wave();
        if (button.hasAttribute('data-rp-v2-state')) {
          this.querySelectorAll('[data-rp-v2-state]').forEach(b => b.setAttribute('aria-pressed', String(b === button)));
          this.querySelectorAll('[data-rp-v2-state-copy]').forEach(p => {p.hidden = p.dataset.rpV2StateCopy !== button.dataset.rpV2State;});
        }
        if (button.dataset.rpV2Look) this.showLook(button.dataset.rpV2Look);
      }, {signal});
      this.addEventListener('pointerdown', event => {
        const stage = event.target.closest('[data-rp-v2-wave]');
        if (!stage || !this.contains(stage)) return;
        if (event.pointerType === 'mouse') this.wave();
        else this.tap = {id:event.pointerId, x:event.clientX, y:event.clientY};
      }, {signal, passive:true});
      this.addEventListener('pointerup', event => {
        const tap = this.tap;
        this.tap = null;
        if (!tap || tap.id !== event.pointerId || Math.hypot(event.clientX-tap.x,event.clientY-tap.y)>12) return;
        if (!event.target.closest('[data-rp-v2-wave]')) return;
        this.gaze(event);
        this.wave();
        clearTimeout(this.gazeTimer);
        this.gazeTimer = setTimeout(() => this.resetGaze(), 700);
      }, {signal, passive:true});
      this.addEventListener('pointercancel', () => {this.tap=null; this.resetGaze();}, {signal});
      this.addEventListener('pointermove', event => {
        if (event.pointerType === 'mouse' && event.target.closest('[data-rp-v2-wave]')) this.gaze(event);
      }, {signal, passive:true});
      this.addEventListener('pointerleave', event => {if(event.pointerType==='mouse') this.resetGaze();}, {signal});
      this.looks = [...this.querySelectorAll('[data-rp-v2-look-template]')];
      if (this.looks.length) this.showLook(this.looks[0].dataset.rpV2LookTemplate);
    }
    gaze(event) {
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const rig=this.querySelector('[data-rp-v2-rig]');
      if (!rig) return;
      const b=rig.getBoundingClientRect();
      // Optional presentation-only rig calibration. Legacy rigs retain their exact linear 5/4 range.
      const rangeX = Number(rig.dataset.rpV2GazeRangeX) || 5;
      const rangeY = Number(rig.dataset.rpV2GazeRangeY) || 4;
      const response = Number(rig.dataset.rpV2GazeResponse) || 0;
      const map = value => { const n = Math.max(-1, Math.min(1, value)); return response ? Math.tanh(response*n)/Math.tanh(response) : n; };
      rig.style.setProperty('--rp-v2-eye-x', `${rangeX*map((event.clientX-b.left-b.width/2)/b.width*2)}px`);
      rig.style.setProperty('--rp-v2-eye-y', `${rangeY*map((event.clientY-b.top-b.height/2)/b.height*2)}px`);
    }
    resetGaze() {
      clearTimeout(this.gazeTimer);
      this.querySelectorAll('[data-rp-v2-rig]').forEach(rig=>{rig.style.removeProperty('--rp-v2-eye-x');rig.style.removeProperty('--rp-v2-eye-y');});
    }
    wave() {
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const rig=this.querySelector('[data-rp-v2-rig]');
      if (!rig) return;
      rig.classList.remove('rp-v2-pdp-waving'); void rig.offsetWidth; rig.classList.add('rp-v2-pdp-waving');
    }
    bindOwner() {
      this.ownerController?.abort();
      this.ownerController = new AbortController();
      this.owner = document.getElementById(this.ownerId);
      this.ownerSection = this.owner?.dataset.section;
      this.galleryCaption?.remove();
      const caption = this.querySelector('[data-rp-v2-gallery-caption]');
      const gallery = this.owner?.querySelector('media-gallery');
      if (caption && gallery) {
        this.galleryCaption = caption.content.firstElementChild.cloneNode(true);
        gallery.after(this.galleryCaption);
      }

      this.owner?.addEventListener('change', event => {
        if (event.target.matches('input[name="id"]') && !event.target.value) this.showVariant('');
      }, {signal:this.ownerController.signal});
      const picker = this.owner?.querySelector('variant-radios, variant-selects');
      const hasSelection = !picker || picker.querySelector('input[type=radio]:checked, option[selected]');
      this.showVariant(hasSelection ? (this.owner?.querySelector('.product-variant-id')?.value || '') : '');
    }
    showVariant(id) {
      this.querySelectorAll('[data-rp-v2-variant]').forEach(card => {
        const selected = card.dataset.rpV2Variant === id;
        card.classList.toggle('rp-v2-pdp-selected', selected);
        if (selected) card.setAttribute('aria-current', 'true'); else card.removeAttribute('aria-current');
      });
      this.querySelectorAll('[data-rp-v2-panel]').forEach(panel => {panel.hidden = panel.dataset.rpV2Panel !== id;});
      const output = this.querySelector('[data-rp-v2-hello-output]');
      if(output&&this.hasAttribute('data-rp-v2-profiles'))this.renderProfile(id,output);
      else if (output) {
        this.tap=null; this.resetGaze();
        const templates = [...this.querySelectorAll('[data-rp-v2-hello]')];
        const template = templates.find(t => t.dataset.rpV2Hello === id) || this.querySelector('[data-rp-v2-fallback]');
        output.replaceChildren(...(template ? [template.content.cloneNode(true)] : []));
        const wave = this.querySelector('[data-rp-v2-wave]');
        if (wave) wave.disabled = !output.querySelector('[data-rp-v2-rig]');
        this.hidden = !output.querySelector('img') && !window.Shopify?.designMode;
      }
      if (this.dataset.rpV2Kind === 'Details') {
        const hasSpecs = !!this.querySelector('.rp-v2-pdp-spec');
        const hasBox = [...this.querySelectorAll('[data-rp-v2-panel]')].some(p => !p.hidden);
        this.hidden = !hasSpecs && !hasBox;
        const boxColumn=this.querySelector('.rp-v2-pdp-box-column');
        if(boxColumn) boxColumn.hidden=!hasBox;
        this.classList.toggle('rp-v2-pdp-details-single', !(hasSpecs && hasBox));
      }
    }
    renderProfile(id,output){
      const all=[...this.querySelectorAll('[data-rp-v2-look-profile]')],enabled=all.filter(t=>t.dataset.rpV2ProfileEnabled==='true'),productIds=this.dataset.rpV2ProductVariants.split(','),preview=this.dataset.rpV2Editor==='true'?(this.editorProfile||this.dataset.rpV2EditorPreview):'';
      const keyCount=key=>enabled.filter(t=>t.dataset.rpV2LookProfile===key).length;
      const valid=t=>/^[a-z0-9][a-z0-9_-]*$/.test(t.dataset.rpV2LookProfile)&&keyCount(t.dataset.rpV2LookProfile)===1&&t.dataset.rpV2ProfileAssembly!=='not_ready';
      const ids=t=>t.dataset.rpV2ProfileVariants.split(',').filter(Boolean);
      const matches=enabled.filter(t=>preview?t.dataset.rpV2LookProfile===preview:ids(t).includes(String(id)));
      const template=matches.length===1&&valid(matches[0])?matches[0]:null;
      const status=template?(template.dataset.rpV2ProfileComplete==='true'?'interactive':'static-fallback'):'unavailable';
      this.dataset.rpV2CurrentVariant=String(id);this.dataset.rpV2ProfileStatus=status;
      const message=this.querySelector('[data-rp-v2-profile-message]');
      if(message){const invalid=enabled.filter(t=>!valid(t)||ids(t).some(id=>!productIds.includes(id)));message.hidden=!!template&&status==='interactive'&&!invalid.length;message.textContent=template?'Profile '+template.dataset.rpV2LookProfile+' / '+status+(invalid.length?' — check duplicate IDs, unverified assembly or invalid variant mappings.':''):'No verified, unambiguous Look Profile for the selected variant. Basic requires its own real no-dock asset.';}
      const token=(template?.dataset.rpV2ProfileId||'missing')+':'+id+':'+(preview||'');
      if(output.dataset.rpV2ProfileToken===token)return;
      output.dataset.rpV2ProfileToken=token;this.tap=null;this.resetGaze();
      output.replaceChildren(...(template?[template.content.cloneNode(true)]:[]));
      const wave=this.querySelector('[data-rp-v2-wave]');if(wave)wave.disabled=true;
      this.hidden=!output.querySelector('img')&&this.dataset.rpV2Editor!=='true';
    }
    showLook(id) {
      const record = this.looks.find(t => t.dataset.rpV2LookTemplate === id);
      if (!record) return;
      this.currentLook = record;
      const name = this.querySelector('[data-rp-v2-look-name]');
      if (name) name.textContent = record.content.querySelector('figcaption')?.textContent || '';
      this.querySelector('[data-rp-v2-look-output]')?.replaceChildren(record.content.querySelector('figure').cloneNode(true));
      this.querySelector('[data-rp-v2-breakdown-output]')?.replaceChildren(record.content.querySelector('[data-rp-v2-breakdown]').cloneNode(true));
      this.querySelectorAll('[data-rp-v2-look]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.rpV2Look === id)));
    }
    disconnectedCallback() {cancelAnimationFrame(this.profileFrame);this.resetGaze(); this.tap=null; this.galleryCaption?.remove(); this.galleryCaption = null; this.ownerController?.abort(); this.ownerController = null; this.controller?.abort(); this.controller = null; this.owner = null; this.looks = []; this.currentLook = null;}
  }
  customElements.define('rp-v2-pdp', RopetPDP);
})();
// Skin existing native controls. This element never creates a purchase control,
// writes an ID/quantity/price. The configured default uses the existing radio click chain.
if (!customElements.get('rp-v2-pdp-purchase')) {
  customElements.define('rp-v2-pdp-purchase', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller=new AbortController();
      const signal=this.controller.signal;
      this.owner=document.getElementById(this.dataset.rpV2Owner);
      this.sectionRoot=this.closest('[id^="shopify-section-"]');
      this.owner?.classList.add('rp-v2-pdp-purchase');
      this.records=[...this.querySelectorAll('[data-rp-v2-card]')];
      const price=this.owner?.querySelector('.product--price-wrapper');
      const template=this.querySelector('[data-rp-v2-price-prompt]');
      if(price && template){this.prompt=template.content.firstElementChild.cloneNode(true);price.prepend(this.prompt);}
      this.sticky=this.sectionRoot?.querySelector('sticky-cart');
      if(this.sticky){
        this.sticky.classList.add('rp-v2-pdp-sticky');
        this.toggle=document.createElement('button');
        this.toggle.type='button';this.toggle.className='rp-v2-pdp-sticky-toggle';
        this.toggle.setAttribute('aria-expanded','false');this.toggle.setAttribute('aria-label','Expand purchase bar');this.toggle.textContent='⌃';
        const panel=this.sticky.querySelector('product-info');
        if(panel?.id)this.toggle.setAttribute('aria-controls',panel.id);
        this.sticky.append(this.toggle);
        this.toggle.addEventListener('click',()=>{
          this.setExpanded(!this.sticky.hasAttribute('data-rp-v2-expanded'));
        },{signal});
      }
      this.queue=()=>{if(!this.frame)this.frame=requestAnimationFrame(()=>{this.frame=0;this.skin();});};
      this.observer=new MutationObserver(this.queue);
      if(this.sectionRoot)this.observer.observe(this.sectionRoot,{subtree:true,childList:true,attributes:true,attributeFilter:['class','value','checked','disabled']});
      this.overlays=new MutationObserver(this.queue);
      this.overlays.observe(document.body,{subtree:true,childList:true,attributes:true,attributeFilter:['open','class','aria-hidden']});
      window.addEventListener('scroll',this.queue,{passive:true,signal});
      window.addEventListener('resize',this.queue,{passive:true,signal});
      document.addEventListener('afterVariantChanged',this.queue,{signal});
      this.skin();
      this.owner?.addEventListener('change', event=>{if(event.isTrusted)this.userSelected=true;},{signal,capture:true});
      Promise.all([customElements.whenDefined('variant-radios'),customElements.whenDefined('product-info')]).then(()=>{
        if(this.isConnected && !signal.aborted)this.defaultFrame=requestAnimationFrame(()=>this.selectConfiguredDefault());
      });
    }
    selectConfiguredDefault(){
      // Only a merchant-selected variant reference, on entry with no URL variant.
      // Clicking the original radio lets Shopify refresh media, price and forms.
      if(!this.isConnected || this.userSelected || new URLSearchParams(location.search).has('variant') || this.owner?.hasAttribute('data-rp-v2-default-applied'))return;
      const record=this.records.find(r=>r.dataset.rpV2Card===this.dataset.rpV2Default);
      const picker=this.owner?.querySelector('variant-radios');
      if(!record || !picker || picker.querySelectorAll('fieldset').length!==1)return;
      const input=[...picker.querySelectorAll('input[type=radio]')].find(r=>r.value===record.dataset.rpV2Title);
      if(!input || input.disabled)return;
      this.owner.setAttribute('data-rp-v2-default-applied','');
      if(input.checked)return;
      input.click();
    }
    setExpanded(open){
      if(!this.sticky || !this.toggle)return;
      if(this.toggle.getAttribute('aria-expanded')===String(open))return;
      this.sticky.toggleAttribute('data-rp-v2-expanded',open);
      this.toggle.setAttribute('aria-expanded',String(open));
      this.toggle.setAttribute('aria-label',open?'Collapse purchase bar':'Expand purchase bar');
      this.toggle.textContent=open?'⌄':'⌃';
    }
    skin(){
      this.owner?.querySelectorAll('variant-radios input[type=radio]').forEach(input=>{
        const record=this.records.find(r=>r.dataset.rpV2Title===input.value);
        const label=input.nextElementSibling;
        if(!record || label?.tagName!=='LABEL' || label.querySelector('.rp-v2-pdp-card-label'))return;
        const text=label.querySelector('.variant-text-wrapper');
        if(!text)return;
        text.prepend(record.content.querySelector('.rp-v2-pdp-card-label').cloneNode(true));
        const image=record.content.querySelector('img');
        if(image)label.prepend(image.cloneNode(true));
        const status=record.content.querySelector('.rp-v2-pdp-card-status');
        if(status)text.append(status.cloneNode(true));
        label.classList.toggle('rp-v2-pdp-card-unavailable',record.dataset.rpV2Available==='false');
        label.classList.add('rp-v2-pdp-card');
      });
      if(!this.sticky)return;
      const id=this.owner?.querySelector('.product-variant-id')?.value;
      const selected=this.owner?.querySelector('variant-radios input:checked,variant-selects option:checked');
      const picker=this.owner?.querySelector('variant-radios,variant-selects');
      // A default-only product has no picker. Resolve presentation from its
      // existing native form ID; never infer a selection for an option product.
      const defaultOnly=this.dataset.rpV2OnlyDefaultVariant==='true' && !picker && this.records.length===1;
      const record=(selected || defaultOnly) ? this.records.find(r=>r.dataset.rpV2Card===id) : null;
      const media=this.sticky.querySelector('.sticky-cart__price-media');
      if(media){
        let label=media.querySelector('.rp-v2-pdp-sticky-label');
        if(!label){label=document.createElement('span');label.className='rp-v2-pdp-sticky-label';media.prepend(label);}
        const value=record?.dataset.rpV2Label || '';
        if(label.textContent!==value)label.textContent=value;
      }
      const withinStage=!this.owner || this.owner.getBoundingClientRect().bottom>0;
      const visible=e=>{const r=e.getBoundingClientRect(),c=getComputedStyle(e);return r.width>0&&r.height>0&&c.display!=='none'&&c.visibility!=='hidden'&&Number(c.opacity)>0;};
      const blocked=[...document.querySelectorAll('dialog[open],.drawer.active,details-modal details[open],header-drawer details[open],[aria-modal="true"]')].some(visible);
      this.sticky.toggleAttribute('data-rp-v2-suppressed',withinStage||blocked||!record);
      if(withinStage||blocked||!this.sticky.classList.contains('open'))this.setExpanded(false);
    }
    disconnectedCallback(){
      this.controller?.abort();this.controller=null;this.observer?.disconnect();this.overlays?.disconnect();cancelAnimationFrame(this.frame);cancelAnimationFrame(this.defaultFrame);this.frame=0;
      this.prompt?.remove();this.toggle?.remove();
      this.owner?.querySelectorAll('.rp-v2-pdp-card-label,.rp-v2-pdp-card-thumb,.rp-v2-pdp-card-status').forEach(e=>e.remove());
      this.owner?.querySelectorAll('.rp-v2-pdp-card').forEach(e=>e.classList.remove('rp-v2-pdp-card','rp-v2-pdp-card-unavailable'));
      this.sticky?.querySelectorAll('.rp-v2-pdp-sticky-label').forEach(e=>e.remove());
      this.sticky?.classList.remove('rp-v2-pdp-sticky');this.sticky?.removeAttribute('data-rp-v2-expanded');this.sticky?.removeAttribute('data-rp-v2-suppressed');
      this.owner?.classList.remove('rp-v2-pdp-purchase');this.owner=null;this.sticky=null;this.records=[];
    }
  });
}
// Floaters are presentation-only. Targets are explicit section-scoped IDs;
// no event is dispatched into the native variant/form/cart system.
if (!customElements.get('rp-v2-pdp-opener')) {
  customElements.define('rp-v2-pdp-opener', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-rp-v2-open]');
        if (!button || !this.contains(button)) return;
        const target=this.dataset.rpV2CompareProduct ? [...document.querySelectorAll('rp-v2-pdp[data-rp-v2-kind=Compare]')].find(e=>e.dataset.rpV2Product===this.dataset.rpV2CompareProduct)?.querySelector('rp-v2-pdp-float') : document.getElementById(button.dataset.rpV2Open);
        target?.open?.(button);
      }, {signal:this.controller.signal});
    }
    disconnectedCallback() {this.controller?.abort(); this.controller = null;}
  });
}
if (!customElements.get('rp-v2-pdp-float')) {
  customElements.define('rp-v2-pdp-float', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.dialog = this.querySelector('dialog');
      this.addEventListener('click', event => {
        if (event.target.closest('[data-rp-v2-close]')) this.dialog.close();
        else if (event.target === this.dialog) {
          const r = this.dialog.getBoundingClientRect();
          if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.dialog.close();
        }
      }, {signal});
      this.dialog?.addEventListener('close', () => {
        this.trigger?.setAttribute('aria-expanded', 'false');
        if (this.trigger?.isConnected) this.trigger.focus({preventScroll:true});
        this.trigger = null;
      }, {signal});
      window.addEventListener('resize', () => {if (this.dialog?.open) this.position();}, {signal});
    }
    open(trigger) {
      if (!this.dialog || this.dialog.open) return;
      this.trigger = trigger;
      trigger.setAttribute('aria-expanded', 'true');
      this.dialog.showModal();
      this.position();
    }
    position() {
      const dialog = this.dialog;
      const r = dialog.getBoundingClientRect();
      const mobile = matchMedia('(max-width: 749px)').matches;
      const t = this.trigger?.getBoundingClientRect();
      let left = (innerWidth-r.width)/2;
      let top = (innerHeight-r.height)/2;
      if (mobile) {
        dialog.style.left = `${left}px`;
        dialog.style.top = 'auto';
        dialog.style.bottom = 'calc(16px + env(safe-area-inset-bottom))';
        return;
      }
      if (this.dataset.rpV2Size === 'policy' && t) {
        left = Math.min(Math.max(16,t.left),innerWidth-r.width-16);
        top = t.bottom+12;
        if (top+r.height>innerHeight-16) top=t.top-r.height-12;
      }
      dialog.style.left = `${Math.max(16,left)}px`;
      dialog.style.top = `${Math.max(16,Math.min(top,innerHeight-r.height-16))}px`;
      dialog.style.bottom = 'auto';
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      if (this.dialog?.open) this.dialog.close();
      this.trigger?.setAttribute('aria-expanded','false');
      this.trigger = null; this.dialog = null;
    }
  });
}